/**
 * RecordingControls — smart wrapper for the live recording surface.
 *
 * Owns the cross-hook plumbing (shared refs, lifecycle state, view-arg
 * assembly) and orchestrates the lifecycle FSMs that drive the recording
 * experience:
 *
 *   useStartRecording        — handleStart (mic + WS + recorder spin-up)
 *   useFinishLifecycle       — handleFinish (drain, stop, finalize-poll)
 *   useRecoveryFlow          — resume / submit / discard for IDB-replayed
 *                              chunks left over from a previous crash
 *   useTestMicRecorder       — 10s side-recorded mic-test loop
 *   useMicAlertHysteresis    — 10s show / 15s hide mic-alert FSM
 *   useBackgroundFlush       — beforeunload + visibilitychange + pagehide
 *                              keepalive pipes that prevent audio loss when
 *                              the OS or browser kills the tab
 *   useRecordingMeetingEvents — SSE projection (processing stage, identified
 *                              speakers, streaming start/stop intent)
 *   useTranscriptStaleWatch  — unanswered-speech seconds for the streaming UI
 *   useOnlineFlush           — reconnect-driven provisional creation +
 *                              pending-chunk flush
 *
 * Forwards a phase tag + the matching prop bundle to the dumb
 * <RecordingControlsView/>, which dispatches to PreRecord (Lexicanon idle),
 * RecordingFocus (Lexicanon recording / paused / finalizing), the recovery
 * banner, or the legacy Voxcanon Card UI.
 *
 * The Voxcanon SceneInsightsDrawer is rendered as a sibling because it is a
 * smart component itself (subscribes to SSE, runs its own queries).
 */
import type { TCoachTone, TStreamControlMessage } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useBlocker, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRecordingState } from "../../contexts/RecordingStateContext"
import { useAudioPipeline } from "../../hooks/useAudioPipeline"
import { useAuth } from "../../hooks/useAuth"
import { useChunkUpload } from "../../hooks/useChunkUpload"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { useDesktopTray } from "../../hooks/useDesktopTray"
import type { SavedRecording } from "../../hooks/useIndexedDB"
import { useNetworkStatus } from "../../hooks/useNetworkStatus"
import { usePermissionState } from "../../hooks/usePermissionState"
import { useRecordingHealthCheck } from "../../hooks/useRecordingHealthCheck"
import { useRemoteChannelStreaming } from "../../hooks/useRemoteChannelStreaming"
import { useSessionKeepalive } from "../../hooks/useSessionKeepalive"
import { useSpeakerHypothesisActions } from "../../hooks/useSpeakerHypothesisActions"
import { useStreamingTranscript } from "../../hooks/useStreamingTranscript"
import { useStreamingWs } from "../../hooks/useStreamingWs"
import { deleteMeetingPermanently, fetchMeeting, fetchSettings, reportFarEndInMic } from "../../lib/api/meetings"
import { languageDisplayName } from "../../lib/language-display"
import {
  clearAutoStartFlag,
  readAutoStartFlag,
  resolveRecordingStart,
  toRecordingMode,
} from "../../lib/meeting-start-config"
import { displayMeetingTitle } from "../../lib/meeting-title"
import { notifyError } from "../../lib/notify"
import { getLabels } from "../../lib/product-labels"
import { queryKeys } from "../../lib/query-keys"
import { IS_TAURI } from "../../lib/runtime-origin"
import { useProduct } from "../../lib/tenant"
import { deriveTrayStatus } from "../../lib/tray-status"
import type { TProcessingStage } from "../FinalizationProgress"
import { MeetingFinalizingView } from "../MeetingSummary/MeetingFinalizingView"
import { SceneInsightsDrawer } from "../SceneInsightsDrawer"
import { SystemAudioPicker } from "../SystemAudioPicker"
import { SystemAudioStatus } from "../SystemAudioStatus"
import {
  countFinalSpeechSegments,
  countStoredSpeechSegments,
  shouldAskToDiscardEmptyRecording,
} from "./empty-recording"
import { useBackgroundFlush } from "./hooks/useBackgroundFlush"
import { useDialogConfig } from "./hooks/useDialogConfig"
import { useFinishLifecycle } from "./hooks/useFinishLifecycle"
import { useMicAlertHysteresis } from "./hooks/useMicAlertHysteresis"
import { useOnlineFlush } from "./hooks/useOnlineFlush"
import { usePauseResume } from "./hooks/usePauseResume"
import { useRecordingMeetingEvents } from "./hooks/useRecordingMeetingEvents"
import { useRecordingViewArgs } from "./hooks/useRecordingViewArgs"
import { useRecoveryFlow } from "./hooks/useRecoveryFlow"
import { useRecoveryLoader } from "./hooks/useRecoveryLoader"
import { useStartRecording } from "./hooks/useStartRecording"
import { useStreamingAdapters } from "./hooks/useStreamingAdapters"
import { useTestMicRecorder } from "./hooks/useTestMicRecorder"
import { isClientSpeaking, useTranscriptStaleWatch } from "./hooks/useTranscriptStaleWatch"
import type { IProps, IRemoteUploadHandle, TRecordingMode, TRecordingState } from "./interfaces"
import { PauseTimeoutModal } from "./PauseTimeoutModal"
import { RecordingControlsView } from "./RecordingControlsView"

export function RecordingControls({
  meetingId,
  onRecordingStopped,
  subject,
  templateId,
  isProvisional = false,
  meetingSubject,
  meetingStartTime,
  onRenameMeeting,
}: IProps): React.JSX.Element | null {
  const company = useCompanyApi()
  const navigate = useNavigate()
  const product = useProduct()
  const { user } = useAuth()
  const { i18n } = useLingui()
  const labels = getLabels(i18n, product)
  const companyId = user?.activeOrganizationId
  // Phase 2 conditional-loopback switch. Resolve org → platform default →
  // hardcoded ON client-side, matching the backend `resolveConditionalLoopbackEnabled`
  // (the merged GET /settings returns both keys, not the code default). Cached +
  // shared with other settings reads.
  const settingsQuery = useCompanyQuery({
    key: queryKeys.settings,
    queryFn: (client) => fetchSettings(client),
    staleTime: 5 * 60_000,
  })
  const conditionalLoopbackEnabled = ((): boolean => {
    const raw =
      settingsQuery.data?.conditional_loopback_enabled ?? settingsQuery.data?.default_conditional_loopback_enabled
    return raw == null ? true : raw === "true"
  })()
  const [sceneInsightsOpen, setSceneInsightsOpen] = useState(false)
  const [error, setError] = useState("")
  const [recovered, setRecovered] = useState<SavedRecording | null>(null)
  const [showFinishConfirm, setShowFinishConfirm] = useState(false)
  // Finalize polling exhausted while the server is still processing. Keeps the
  // finalize surface mounted with a "taking longer than usual" notice instead of
  // silently dropping to idle (which looked identical to success). See §1.2.
  const [finalizeDelayed, setFinalizeDelayed] = useState(false)
  // Async-in-flight flags for the Finish + Submit-recovery buttons so they show a
  // spinner and are disabled for the duration (double-click protection UX). See §1.5.
  const [finishing, setFinishing] = useState(false)
  const [submittingRecovery, setSubmittingRecovery] = useState(false)
  const [processingStage, setProcessingStage] = useState<TProcessingStage | null>(null)
  const [identifiedSpeakers, setIdentifiedSpeakers] = useState<string[]>([])
  const [state, setState] = useState<TRecordingState>("idle")

  const { isOnline, reportSuccess, reportFailure } = useNetworkStatus()
  const [provisionalPending, setProvisionalPending] = useState(isProvisional)
  const { setRecordingActive } = useRecordingState()

  const initialDialogConfig = useDialogConfig(meetingId)
  // useDialogConfig does not carry autoStart. Read the same sessionStorage
  // value here so Full can start once, only when the dialog set the flag.
  const autoStart = useMemo(() => {
    if (typeof window === "undefined") return false
    return readAutoStartFlag(sessionStorage.getItem(`meeting-config:${meetingId}`))
  }, [meetingId])
  const companyDefaultLanguage =
    typeof settingsQuery.data?.default_language === "string" ? settingsQuery.data.default_language : undefined
  const startDefaults = useMemo(
    () =>
      resolveRecordingStart({
        stored: initialDialogConfig
          ? {
              langs: initialDialogConfig.langs,
              autoDetect: initialDialogConfig.autoDetect,
              mode: initialDialogConfig.mode,
            }
          : null,
        companyDefaultLanguage,
      }),
    [companyDefaultLanguage, initialDialogConfig],
  )

  const [mode, setMode] = useState<TRecordingMode>(toRecordingMode(startDefaults.mode))
  const [streamingAvailable, setStreamingAvailable] = useState(false)
  const [showTranscript, setShowTranscript] = useState(true)
  const [languages, setLanguages] = useState<string[]>(startDefaults.languages)
  const appliedStartDefaults = useRef(Boolean(initialDialogConfig))
  useEffect(() => {
    if (appliedStartDefaults.current) return
    if (!settingsQuery.data) return
    appliedStartDefaults.current = true
    setMode(toRecordingMode(startDefaults.mode))
    setLanguages(startDefaults.languages)
  }, [settingsQuery.data, startDefaults])
  const [streamingAdapterList, setStreamingAdapterList] = useState<{ name: string; label: string }[]>([])
  const [defaultAdapterName, setDefaultAdapterName] = useState<string>("")
  const [selectedBackend, setSelectedBackend] = useState<string | null>(null)

  const stateRef = useRef<TRecordingState>("idle")
  stateRef.current = state
  const isOnlineRef = useRef(isOnline)
  isOnlineRef.current = isOnline
  const provisionalPendingRef = useRef(provisionalPending)
  provisionalPendingRef.current = provisionalPending
  // Whether we're in live/streaming mode — read inside the remote capture chain
  // to decide if the far-end transcript WS should open (the durable file is
  // always REST, independent of streaming).
  const streamingRef = useRef(mode === "streaming")
  streamingRef.current = mode === "streaming"
  // Far-end capture-time offset (s), written when the remote channel locks and
  // sent with its first chunk so the backend can align the mix.
  const remoteOffsetSecRef = useRef<number | undefined>(undefined)
  // Remote-channel upload failures must never clobber the mic's user-facing
  // error — the far-end is best-effort relative to the room recording.
  const noopSetError = useCallback((): void => {}, [])

  const {
    streamRef,
    audioCtxRef,
    destinationRef,
    currentVolumeRef,
    startTimeRef,
    pausedElapsedRef,
    monitoring,
    devices,
    selectedDevice,
    setSelectedDevice,
    elapsed,
    setElapsed,
    volume,
    spectrum,
    boostedVolume,
    boostEnabled,
    startMonitoring,
    requestWakeLock,
    releaseWakeLock,
    startElapsedTimer,
    stopElapsedTimer,
    getMimeType,
    handleBoostToggle,
  } = useAudioPipeline({ setError })

  const {
    chunksRef,
    activeMimeTypeRef,
    uploadingRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    absoluteBaseRef,
    uploading,
    uploadCount,
    setUploadCount,
    idbWarning,
    setIdbWarning,
    doChunkUpload,
    startPeriodicSave,
    stopPeriodicSave,
    startChunkUploadTimer,
    stopChunkUploadTimer,
  } = useChunkUpload({
    meetingId,
    subject,
    templateId,
    isOnlineRef,
    provisionalPendingRef,
    startTimeRef,
    reportSuccess,
    reportFailure,
    setError,
  })

  // Desktop hybrid: a second, fully independent uploader for the far-end
  // ("remote"/system-loopback) channel. Separate buffer/cursor/timers mean the
  // mic path above is provably untouched; the far-end file rides the same
  // offline persistence stack (IDB + REST chunk upload + recovery).
  const {
    chunksRef: remoteChunksRef,
    activeMimeTypeRef: remoteActiveMimeTypeRef,
    lastUploadedChunkIndexRef: remoteLastUploadedChunkIndexRef,
    uploadBatchIndexRef: remoteUploadBatchIndexRef,
    absoluteBaseRef: remoteAbsoluteBaseRef,
    doChunkUpload: remoteDoChunkUpload,
    startPeriodicSave: startRemotePeriodicSave,
    stopPeriodicSave: stopRemotePeriodicSave,
    startChunkUploadTimer: startRemoteChunkUploadTimer,
    stopChunkUploadTimer: stopRemoteChunkUploadTimer,
  } = useChunkUpload({
    meetingId,
    subject,
    templateId,
    isOnlineRef,
    provisionalPendingRef,
    startTimeRef,
    reportSuccess,
    reportFailure,
    setError: noopSetError,
    channel: "remote",
    offsetSecRef: remoteOffsetSecRef,
  })

  // Drive the remote uploader's timers from the tap lifecycle: start them when
  // the far-end locks, stop them when the capture chain tears down.
  const handleRemoteLock = useCallback((): void => {
    startRemotePeriodicSave()
    startRemoteChunkUploadTimer()
  }, [startRemotePeriodicSave, startRemoteChunkUploadTimer])

  const handleRemoteStop = useCallback((): void => {
    stopRemoteChunkUploadTimer()
    stopRemotePeriodicSave()
  }, [stopRemoteChunkUploadTimer, stopRemotePeriodicSave])

  const {
    segments: liveSegments,
    interimText,
    interimSpeaker,
    progressiveSummary,
    progressiveSummaryCount,
    progressiveSummaryAt,
    facilitatorSuggestion,
    facilitatorHistory,
    speakerHypotheses,
    languageSignal,
    languageSuggestion,
    acceptLanguageSwitch,
    keepCurrentLanguage,
    meetingLanguage,
    attachWs,
    detachWs,
    attachRemoteWs,
    detachRemoteWs,
  } = useStreamingTranscript(meetingId, companyId)

  // First MediaRecorder chunk carries the WebM/Opus init header. Cached here so
  // pause/resume AND a WS auto-reconnect — both of which reuse the running
  // recorder but open a NEW WebSocket — can re-send the header on the fresh
  // connection; without it the backend gets headerless Opus frames and rejects
  // them ("Invalid audio type"), permanently killing live transcription.
  const firstHeaderChunkRef = useRef<Blob | null>(null)
  // Stable so it doesn't churn useStreamingWs's openStreamingWs identity.
  const getHeaderChunk = useCallback((): Blob | null => firstHeaderChunkRef.current, [])

  const { wsRef, wsReconnecting, openStreamingWs, resetWsState, handleStreamingStopped, handleStreamingFallback } =
    useStreamingWs({
      meetingId,
      companyId,
      languages,
      backend: selectedBackend,
      getHeaderChunk,
      stateRef,
      attachWs,
      detachWs,
      setMode,
      setError,
    })

  // Hybrid capture: in the Tauri desktop app, a second "remote" channel captures
  // the system loopback (the remote meeting participants) alongside the mic. Web
  // builds skip this entirely (IS_TAURI === false). The mic path is untouched.
  const {
    startRemote,
    stopRemote,
    status: remoteAudioStatus,
    setSource: setRemoteAudioSource,
    farEndInMic,
    speakersLatched,
  } = useRemoteChannelStreaming({
    meetingId,
    companyId,
    languages,
    backend: selectedBackend,
    attachWs: attachRemoteWs,
    detachWs: detachRemoteWs,
    // Excludes the mic from the remote far-end auto-detect — virtual mixers expose
    // the mic as a render endpoint (flagged Communications default) that the hunt
    // would otherwise lock onto. Matched by the selected input device's label.
    primaryMicLabel: devices.find((d) => d.deviceId === selectedDevice)?.label,
    // Persistence wiring: every far-end recorder chunk lands in the remote
    // uploader's buffer (REST = single source of truth); the WS is gated to
    // streaming; the lock fires the offset + the remote timers.
    chunksRef: remoteChunksRef,
    mimeTypeRef: remoteActiveMimeTypeRef,
    offsetSecRef: remoteOffsetSecRef,
    recordingStartRef: startTimeRef,
    streamingRef,
    // The raw mic stream feeds the far-end-in-mic coherence detector alongside the
    // loopback. When the switch is on and the meeting latches to speakers-mode, the
    // remote LIVE transcript is suppressed (capture is never affected).
    micStreamRef: streamRef,
    conditionalLoopbackEnabled,
    onFarEndVerdict: (speakersMode) => {
      if (company && meetingId) void reportFarEndInMic(company, meetingId, speakersMode).catch(() => {})
    },
    onLock: handleRemoteLock,
    onStop: handleRemoteStop,
  })
  const remoteActiveRef = useRef(false)
  const [audioPickerOpen, setAudioPickerOpen] = useState(false)

  // Bundled handle onto the far-end channel, threaded into the lifecycle FSMs
  // (finish / online-flush / background-flush / recovery). Stable — every field
  // is a stable ref or stable callback — so it never churns the consumers. Only
  // meaningfully populated on desktop; web simply never locks the remote channel.
  const remoteUpload = useMemo<IRemoteUploadHandle>(
    () => ({
      chunksRef: remoteChunksRef,
      activeMimeTypeRef: remoteActiveMimeTypeRef,
      lastUploadedChunkIndexRef: remoteLastUploadedChunkIndexRef,
      uploadBatchIndexRef: remoteUploadBatchIndexRef,
      absoluteBaseRef: remoteAbsoluteBaseRef,
      offsetSecRef: remoteOffsetSecRef,
      doChunkUpload: remoteDoChunkUpload,
      stopRemote,
    }),
    [
      remoteChunksRef,
      remoteActiveMimeTypeRef,
      remoteLastUploadedChunkIndexRef,
      remoteUploadBatchIndexRef,
      remoteAbsoluteBaseRef,
      remoteDoChunkUpload,
      stopRemote,
    ],
  )

  // Cross-hook shared refs — touched by start, finish, recovery, and the
  // background-flush effects. Keeping them here means each lifecycle hook
  // receives them as deps without owning their identity.
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const lastSegmentAtRef = useRef(Date.now())
  const lastAudioAtRef = useRef(Date.now())
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Late-bound callback for the pause-timeout cull. Wired after
  // useFinishLifecycle returns `handleFinish`, since handleFinish doesn't
  // exist yet when usePauseResume is constructed.
  const onPauseTimeoutRef = useRef<(() => void) | null>(null)

  const sessionHealth = useSessionKeepalive()
  const health = useRecordingHealthCheck({
    isRecording: state === "recording" || state === "paused",
    isPaused: state === "paused",
    wsReadyState: wsRef.current?.readyState ?? null,
    lastSegmentAtRef,
    lastAudioAtRef,
    sessionHealth,
    isBatchMode: mode === "batch",
  })

  // Mirror the live recording state to the desktop menu bar (tray) icon. Pure
  // derivation from signals already in scope; no-op off the desktop shell.
  const trayStatus = useMemo(
    () =>
      deriveTrayStatus({
        state,
        error,
        health: health.status,
        wsReconnecting,
        remotePhase: remoteAudioStatus.phase,
      }),
    [state, error, health.status, wsReconnecting, remoteAudioStatus.phase],
  )
  useDesktopTray(trayStatus)

  const { micAlert } = useMicAlertHysteresis({
    volume,
    monitoring,
    selectedDevice,
    boostEnabled,
    handleBoostToggle,
  })

  const { testRecording, testCountdown, testAudioUrl, handleTestMic } = useTestMicRecorder({
    destinationRef,
    getMimeType,
  })

  const isActive = state === "recording" || state === "paused"

  // Don't let an accidental in-app navigation, back/forward (incl. a mobile
  // swipe-back), reload or tab close interrupt a live recording — confirm first.
  useBlocker({
    shouldBlockFn: () =>
      isActive ? !window.confirm(i18n._(msg`A recording is in progress. Leave and stop it?`)) : false,
    enableBeforeUnload: () => isActive,
  })

  const clientSpeaking = state === "recording" && isClientSpeaking(volume)
  const { transcriptStaleSec } = useTranscriptStaleWatch({
    mode,
    isActive,
    liveSegmentCount: liveSegments.length,
    interimText,
    speaking: clientSpeaking,
    lastSegmentAtRef,
  })

  useStreamingAdapters({
    company,
    setStreamingAvailable,
    setStreamingAdapterList,
    setDefaultAdapterName,
    setMode,
  })

  useRecoveryLoader({ meetingId, setRecovered })

  // ---------------------------------------------------------------------------
  // Lifecycle hooks — extracted FSMs that receive the shared refs/state by
  // closure-via-args. The wrapper still decides WHEN handlers fire (button
  // onClick wiring + arg bundles below).
  // ---------------------------------------------------------------------------

  const { handleStart } = useStartRecording({
    meetingId,
    mode,
    monitoring,
    selectedDevice,
    audioCtxRef,
    streamRef,
    destinationRef,
    currentVolumeRef,
    startTimeRef,
    pausedElapsedRef,
    mediaRecorderRef,
    wsRef,
    chunksRef,
    activeMimeTypeRef,
    firstHeaderChunkRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    lastSegmentAtRef,
    lastAudioAtRef,
    startMonitoring,
    getMimeType,
    requestWakeLock,
    startElapsedTimer,
    startPeriodicSave,
    startChunkUploadTimer,
    setIdbWarning,
    resetWsState,
    openStreamingWs,
    setState,
    setRecordingActive,
    setRecovered,
    setError,
    sessionHealthStatus: sessionHealth.status,
    i18n,
  })

  const {
    handlePause,
    handleContinue,
    pauseConfirmOpen,
    pauseRemainingSeconds,
    handleConfirmKeepPaused,
    handleConfirmFinalize,
  } = usePauseResume({
    meetingId,
    mode,
    company,
    elapsed,
    mediaRecorderRef,
    wsRef,
    firstHeaderChunkRef,
    startTimeRef,
    pausedElapsedRef,
    heartbeatIntervalRef,
    startElapsedTimer,
    stopElapsedTimer,
    startPeriodicSave,
    stopPeriodicSave,
    startChunkUploadTimer,
    stopChunkUploadTimer,
    openStreamingWs,
    setState,
    onPauseTimeoutRef,
  })

  // Poll exhausted but the server is still finalizing: stay on the finalize
  // surface and flip to the "taking longer than usual" notice. Crucially we do
  // NOT call onRecordingStopped() here — that would navigate away as if the
  // summary were ready. The meeting will surface naturally once the server
  // finishes (the route refetches / SSE flips status).
  const handleFinalizeTimeout = useCallback((): void => {
    setFinalizeDelayed(true)
  }, [])

  // Read at stop time. The elapsed timer and the live transcript keep moving
  // until finish runs, and the callback itself fires only after the upload and
  // the finalize poll (ADR 0034: that path is unchanged).
  const elapsedRef = useRef(elapsed)
  elapsedRef.current = elapsed
  const liveSegmentsRef = useRef(liveSegments)
  liveSegmentsRef.current = liveSegments
  const onRecordingStoppedRef = useRef(onRecordingStopped)
  onRecordingStoppedRef.current = onRecordingStopped

  const handleRecordingStopped = useCallback((): void => {
    const seconds = elapsedRef.current
    const liveCount = countFinalSpeechSegments(liveSegmentsRef.current)
    const online = isOnlineRef.current
    const provisional = provisionalPendingRef.current
    onRecordingStoppedRef.current?.()
    // Offline and not-yet-uploaded stops persist to IndexedDB. Asking to
    // delete then would throw away a recording that never reached the server.
    if (!online || provisional) return
    if (!company) return
    // Live speech, a long recording, or a nonsense duration: keep it.
    if (!shouldAskToDiscardEmptyRecording(seconds, liveCount)) return
    const api = company
    // Batch mode never fills the live transcript. Read the saved meeting
    // before asking, and stay quiet if that read fails or already has speech.
    void fetchMeeting(api, meetingId)
      .then((meeting) => {
        const stored = countStoredSpeechSegments(meeting)
        if (stored === null || stored > 0) return
        modals.openConfirmModal({
          title: i18n._(msg`Keep this recording?`),
          children: (
            <Text size="sm">{i18n._(msg`This recording is ${seconds} seconds long and has no speech. Keep it?`)}</Text>
          ),
          labels: { cancel: i18n._(msg`Keep`), confirm: i18n._(msg`Delete`) },
          confirmProps: { color: "danger" },
          onConfirm: () => {
            void deleteMeetingPermanently(api, meetingId)
              .then(() => {
                navigate({ to: "/" })
              })
              .catch((err: unknown) => {
                notifyError(err, i18n._(msg`Delete failed`))
              })
          },
        })
      })
      .catch(() => {
        // Can't tell whether anyone spoke. Leave the meeting where it is.
      })
  }, [company, i18n, meetingId, navigate])

  const { handleFinish } = useFinishLifecycle({
    meetingId,
    subject,
    uploadBatchIndexRef,
    absoluteBaseRef,
    templateId,
    mode,
    company,
    isOnlineRef,
    provisionalPendingRef,
    mediaRecorderRef,
    wsRef,
    chunksRef,
    uploadingRef,
    startTimeRef,
    heartbeatIntervalRef,
    doChunkUpload,
    stopChunkUploadTimer,
    stopElapsedTimer,
    stopPeriodicSave,
    releaseWakeLock,
    handleStreamingStopped,
    setShowFinishConfirm,
    setState,
    setRecordingActive,
    setUploadCount,
    setError,
    setProcessingStage,
    onRecordingStopped: handleRecordingStopped,
    onFinalizeTimeout: handleFinalizeTimeout,
    remoteUpload,
    i18n,
  })

  // Wire the pause-timeout cull to handleFinish now that it exists.
  onPauseTimeoutRef.current = () => {
    handleFinish().catch(() => undefined)
  }

  const { handleResumeRecovery, handleSubmitRecovery, handleDiscardRecovery } = useRecoveryFlow({
    meetingId,
    recovered,
    company,
    streamRef,
    startTimeRef,
    absoluteBaseRef,
    pausedElapsedRef,
    mediaRecorderRef,
    chunksRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    provisionalPendingRef,
    lastAudioAtRef,
    getMimeType,
    doChunkUpload,
    startElapsedTimer,
    startPeriodicSave,
    startChunkUploadTimer,
    setElapsed,
    setRecovered,
    setProvisionalPending,
    setState,
    setRecordingActive,
    setError,
    onRecordingStopped,
    remoteUpload,
    i18n,
  })

  useBackgroundFlush({
    state,
    meetingId,
    subject,
    templateId,
    chunksRef,
    activeMimeTypeRef,
    startTimeRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    absoluteBaseRef,
    provisionalPendingRef,
    doChunkUpload,
    remoteUpload,
  })

  useRecordingMeetingEvents({
    meetingId,
    companyId,
    setProcessingStage,
    setIdentifiedSpeakers,
    handleStreamingStopped,
    handleStreamingFallback,
  })

  useOnlineFlush({
    meetingId,
    subject,
    templateId,
    isOnline,
    provisionalPending,
    company,
    stateRef,
    chunksRef,
    doChunkUpload,
    setProvisionalPending,
    remoteUpload,
  })

  // Drive the hybrid "remote" channel from the recording lifecycle: capture the
  // system loopback whenever we're recording on desktop, tear it down otherwise.
  // Capture runs in batch + offline too (the far-end must survive going offline);
  // the live transcript WS is gated to streaming mode inside the capture chain.
  //
  // "finalizing" deliberately keeps the channel active: handleFinish owns the
  // teardown there (drainRemoteFinal → stopRemote, which must complete its final
  // upload before the room final triggers processing). Letting this effect race
  // a second stopRemote in could drop the far-end's trailing chunk.
  useEffect(() => {
    const shouldRun = IS_TAURI && (state === "recording" || state === "finalizing")
    if (shouldRun && !remoteActiveRef.current) {
      console.info("[remote-capture] lifecycle: starting remote channel", { isTauri: IS_TAURI, state, mode })
      remoteActiveRef.current = true
      startRemote().catch(() => {
        remoteActiveRef.current = false
      })
    } else if (!shouldRun && remoteActiveRef.current) {
      console.info("[remote-capture] lifecycle: stopping remote channel", { isTauri: IS_TAURI, state, mode })
      remoteActiveRef.current = false
      stopRemote().catch(() => undefined)
    }
  }, [state, mode, startRemote, stopRemote])

  // biome-ignore lint/correctness/useExhaustiveDependencies: wsRef is a stable ref
  useEffect(() => {
    return (): void => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      stopRemote().catch(() => undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only cleanup; wsRef + stopRemote are stable
  }, [])

  // Facilitator coach ("Worth asking") — config persisted per device; pushed to
  // the live session over the streaming WS. On by default.
  const [coachEnabled, setCoachEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return true
    return localStorage.getItem("lex:coach-enabled") !== "false"
  })
  const [coachTone, setCoachTone] = useState<TCoachTone>(() => {
    if (typeof window === "undefined") return "balanced"
    const stored = localStorage.getItem("lex:coach-tone")
    return stored === "gentle" || stored === "challenger" ? stored : "balanced"
  })
  const [coachAsking, setCoachAsking] = useState(false)

  const sendWsControl = useCallback(
    (payload: TStreamControlMessage): boolean => {
      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(payload))
        return true
      }
      return false
    },
    [wsRef],
  )

  // Push coach config whenever it changes mid-recording (and on entering the
  // recording state). The server defaults to enabled+balanced, so a message
  // dropped before the socket is open is harmless.
  useEffect(() => {
    if (state !== "recording") return
    sendWsControl({ type: "coach_config", enabled: coachEnabled, tone: coachTone })
  }, [state, coachEnabled, coachTone, sendWsControl])

  // Clear the "asking" pending state as soon as a suggestion lands.
  useEffect(() => {
    if (facilitatorSuggestion) setCoachAsking(false)
  }, [facilitatorSuggestion])

  // The "taking longer than usual" notice only belongs to an in-flight finalize.
  // Clear it whenever we leave the finalizing state (e.g. a fresh recording).
  useEffect(() => {
    if (state !== "finalizing") setFinalizeDelayed(false)
  }, [state])

  const handleCoachToggle = useCallback((enabled: boolean): void => {
    setCoachEnabled(enabled)
    if (typeof window !== "undefined") localStorage.setItem("lex:coach-enabled", String(enabled))
  }, [])

  const handleCoachToneChange = useCallback((tone: TCoachTone): void => {
    setCoachTone(tone)
    if (typeof window !== "undefined") localStorage.setItem("lex:coach-tone", tone)
  }, [])

  const handleCoachAsk = useCallback((): void => {
    if (sendWsControl({ type: "coach_ask" })) {
      setCoachAsking(true)
      window.setTimeout(() => setCoachAsking(false), 8000)
    }
  }, [sendWsControl])

  // Live speaker-recognition confirm/reject — the single owner (pending keys +
  // cross-surface cache invalidation), shared with the post-meeting review path.
  // Threaded into both the focus view (SpeakerHypothesisStrip) and the legacy
  // panel (LiveTranscriptPanel).
  const { pending: hypothesisPending, onAction: handleHypothesisAction } = useSpeakerHypothesisActions(meetingId)

  const isLite = user?.experienceMode === "lite"
  // biome-ignore lint/suspicious/noExplicitAny: PermissionName "microphone" is widely supported but not in lib.dom yet
  const micPermission = usePermissionState("microphone" as any) // eslint-disable-line @typescript-eslint/no-explicit-any

  const workspaceDefaultLanguage = useMemo((): string | undefined => {
    const raw = settingsQuery.data?.default_language
    if (typeof raw !== "string" || raw === "multi") return undefined
    const first = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)[0]
    return first
  }, [settingsQuery.data])

  const { phase, useLexicanonFocus, prerecordArgs, focusArgs, recoveryArgs, legacyArgs } = useRecordingViewArgs({
    product,
    i18n,
    meetingLabel: labels.meeting,
    meetingId,
    meetingSubject,
    meetingStartTime,
    subject,
    onRenameMeeting,
    state,
    mode,
    recovered,
    error,
    setError,
    finishing,
    setFinishing,
    submittingRecovery,
    setSubmittingRecovery,
    showFinishConfirm,
    setShowFinishConfirm,
    processingStage,
    initialDialogConfig,
    languages,
    setLanguages,
    setMode,
    monitoring,
    devices,
    selectedDevice,
    setSelectedDevice,
    volume,
    spectrum,
    boostedVolume,
    boostEnabled,
    startMonitoring,
    handleBoostToggle,
    elapsed,
    isOnline,
    wsReconnecting,
    health,
    liveSegments,
    interimText,
    interimSpeaker,
    transcriptStaleSec,
    progressiveSummary,
    progressiveSummaryCount,
    progressiveSummaryAt,
    handleStart,
    handlePause,
    handleContinue,
    handleFinish,
    handleResumeRecovery,
    handleSubmitRecovery,
    handleDiscardRecovery,
    coachEnabled,
    coachTone,
    facilitatorSuggestion,
    facilitatorHistory,
    coachAsking,
    handleCoachToggle,
    handleCoachToneChange,
    handleCoachAsk,
    speakerHypotheses,
    hypothesisPending,
    handleHypothesisAction,
    idbWarning,
    uploading,
    uploadCount,
    streamingAvailable,
    streamingAdapterList,
    selectedBackend,
    defaultAdapterName,
    setSelectedBackend,
    identifiedSpeakers,
    micAlert,
    showTranscript,
    setShowTranscript,
    testRecording,
    testCountdown,
    testAudioUrl,
    handleTestMic,
    setSceneInsightsOpen,
    isLite,
    languageSignal,
    meetingLanguage,
    workspaceDefaultLanguage,
    languageSuggestion,
    onAcceptLanguageSwitch: acceptLanguageSwitch,
    onKeepCurrentLanguage: keepCurrentLanguage,
  })

  // Lite always auto-starts. Full auto-starts only when the dialog set
  // autoStart (the mic was already granted). Prompt and denied stay on
  // PreRecord so the permission click remains a user gesture. That path only
  // calls startMonitoring — it does not open the recorder or the live socket.
  const shouldAutoStart = isLite || autoStart
  const micPrimeStartedRef = useRef(false)
  useEffect(() => {
    if (!shouldAutoStart) return
    if (phase !== "prerecord") return
    if (provisionalPending && !isOnline) return
    if (micPermission !== "granted" && micPermission !== "unknown") return
    if (monitoring) return
    if (micPrimeStartedRef.current) return
    if (!selectedDevice) return
    micPrimeStartedRef.current = true
    startMonitoring(selectedDevice).catch(() => {
      micPrimeStartedRef.current = false
    })
  }, [shouldAutoStart, phase, provisionalPending, isOnline, micPermission, monitoring, selectedDevice, startMonitoring])

  // One-shot once the mic is live. Never twice, never while the meeting is
  // provisional and offline, never when a start error is already showing.
  // Clear autoStart as soon as this calls handleStart, so leaving and coming
  // back in the same tab does not start again. Lite has no flag; its config
  // is left as stored and it still starts from the experience mode.
  const autoStartedRef = useRef(false)
  useEffect(() => {
    if (autoStartedRef.current) return
    if (!shouldAutoStart) return
    if (phase !== "prerecord") return
    if (!monitoring) return
    if (provisionalPending && !isOnline) return
    if (error.length > 0) return
    autoStartedRef.current = true
    handleStart().catch(() => undefined)
    const storageKey = `meeting-config:${meetingId}`
    const raw = sessionStorage.getItem(storageKey)
    const cleared = clearAutoStartFlag(raw)
    if (cleared !== null && cleared !== raw) {
      sessionStorage.setItem(storageKey, cleared)
    }
  }, [shouldAutoStart, phase, monitoring, provisionalPending, isOnline, error, handleStart, meetingId])

  // Toast when the live language actually flips. Skip the first committed value
  // if it matches the dialog's initial language so opening a meeting doesn't
  // toast "Switched to Dutch".
  const notifiedLanguageRef = useRef<string | undefined>(languages[0])
  useEffect(() => {
    if (!meetingLanguage) return
    if (notifiedLanguageRef.current === meetingLanguage) return
    notifiedLanguageRef.current = meetingLanguage
    const name = languageDisplayName(meetingLanguage, i18n.locale)
    notifications.show({
      color: "info",
      message: i18n._(msg`Switched to ${name}`),
    })
  }, [meetingLanguage, i18n])

  // Lexicanon: once the user hits Finish, show the SINGLE standalone finalize
  // surface (the same `MeetingFinalizingView` the meeting route renders once the
  // server status flips) instead of the in-cockpit finalize card. This kills the
  // jarring inline→"modal" flip mid-finalize — the surface is identical before
  // and after the route takes over at status `parsed`. The recording URL/chapters
  // aren't ready yet, so the player is omitted; the stepper + skeleton carry it.
  // Stage is capped below "complete": the analysis hasn't landed while we're
  // still finalizing, so a stray `complete` SSE can't flash a premature "Done".
  // Lite keeps the in-cockpit FinalizationProgress on LiteRecordingFocus.
  if (useLexicanonFocus && state === "finalizing" && !isLite) {
    return (
      <MeetingFinalizingView
        subject={displayMeetingTitle(
          [meetingSubject, subject].find((value) => Boolean(value?.trim())) ?? "",
          null,
          i18n,
        )}
        startTime={meetingStartTime ?? null}
        segments={liveSegments}
        stage={processingStage === "complete" ? "analyzing" : processingStage}
        delayed={finalizeDelayed}
        meetingId={meetingId}
      />
    )
  }

  return (
    <>
      <RecordingControlsView
        phase={phase}
        product={product}
        prerecordArgs={prerecordArgs}
        focusArgs={focusArgs}
        recoveryArgs={recoveryArgs}
        legacyArgs={legacyArgs}
        isLite={isLite}
        autoStart={autoStart}
        provisionalOffline={provisionalPending && !isOnline}
      />
      {product === "voxcanon" && companyId && (
        <SceneInsightsDrawer
          opened={sceneInsightsOpen}
          onClose={() => setSceneInsightsOpen(false)}
          meetingId={meetingId}
          companyId={companyId}
          isLive={state === "recording" || state === "paused" || state === "finalizing"}
        />
      )}
      <PauseTimeoutModal
        opened={pauseConfirmOpen}
        remainingSeconds={pauseRemainingSeconds}
        onKeepPaused={handleConfirmKeepPaused}
        onFinalize={handleConfirmFinalize}
      />
      {/* Hybrid system-audio (desktop only): floating "capturing from X" indicator
          + the output picker. Auto-detect + auto-switch live in the hook; this is
          the reassurance + manual override. */}
      {IS_TAURI && (
        <>
          <SystemAudioStatus
            phase={remoteAudioStatus.phase}
            sourceName={remoteAudioStatus.sourceName}
            onChangeSource={() => setAudioPickerOpen(true)}
            farEndMode={farEndInMic.mode}
            meanCoherence={farEndInMic.diagnostics?.meanCoherence}
            speakersLatched={speakersLatched}
          />
          <SystemAudioPicker
            opened={audioPickerOpen}
            targets={remoteAudioStatus.targets}
            currentId={remoteAudioStatus.sourceId}
            onSelect={(id) => {
              setAudioPickerOpen(false)
              void setRemoteAudioSource(id)
            }}
            onClose={() => setAudioPickerOpen(false)}
          />
        </>
      )}
    </>
  )
}
